import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CoachingCaseCard, CoachingDifficulty } from '@euler/model/coaching';
import { ReactNode, memo, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { formatName, levelColor, levelName, turnsLabel } from '../copy';
import { Chip } from './Chip';
import { LevelBar } from './LevelBar';

/**
 * One library case: its category, title, caption, format and length. A
 * suggestion names the level to try; the catalog shows the levels on offer.
 */
export const CaseCard = memo(
  ({
    card,
    level,
    footer,
    onPress,
  }: {
    card: CoachingCaseCard;
    /** The level suggested; without it the offered levels are shown. */
    level?: CoachingDifficulty;
    footer?: ReactNode;
    onPress: () => void;
  }) => {
    const styles = useStyles();
    const { rpx } = useResponsiveContext();
    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        style={({ pressed }) => [styles.card, pressed && styles.pressed]}
      >
        <View style={styles.top}>
          <View style={styles.category}>
            <Chip text={card.category.name} color={aiColors.primary} />
            {level == null ? (
              <Typography.Text
                size={rpx(11)}
                color={aiColors.mutedForeground}
                numberOfLines={1}
              >
                {card.parentCategory.name}
              </Typography.Text>
            ) : null}
          </View>
          {level != null ? (
            <Chip text={levelName(level)} color={levelColor(level)} />
          ) : (
            <LevelBar levels={card.levels} />
          )}
        </View>
        <Typography.Text
          size={rpx(14)}
          bold
          color={aiColors.foreground}
          style={styles.title}
        >
          {card.title}
        </Typography.Text>
        <Typography.Text
          size={rpx(12)}
          color={aiColors.mutedForeground}
          numberOfLines={2}
          lineHeight={rpx(18)}
          style={styles.caption}
        >
          {card.caption}
        </Typography.Text>
        <View style={styles.meta}>
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {formatName(card.format)}
          </Typography.Text>
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {turnsLabel(card.expectedTurns)}
          </Typography.Text>
        </View>
        {footer ?? null}
      </Pressable>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        card: {
          flex: 1,
          borderRadius: rpx(14),
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          padding: rpx(16),
        },
        pressed: { opacity: 0.85 },
        top: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: rpx(8),
        },
        category: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(6),
          flexShrink: 1,
        },
        title: { marginTop: rpx(10) },
        caption: { marginTop: rpx(4) },
        meta: { flexDirection: 'row', gap: rpx(12), marginTop: rpx(12) },
      }),
    [rpx],
  );
}
